import type {
  CreateFormativeTaskSessionInput,
  FormativeTaskSession,
  FormativeTaskSubmission,
  FormativeTaskSubmissionResult,
} from './types'

function encodeIdPart(value: string): string {
  return encodeURIComponent(value)
}

export function createFormativeTaskSessionId(input: Pick<
  CreateFormativeTaskSessionInput,
  'studentId' | 'stageId' | 'taskId' | 'contentVersion'
>): string {
  return [
    'formative',
    input.studentId,
    input.stageId,
    input.taskId,
    input.contentVersion,
  ].map(encodeIdPart).join(':')
}

export function createFormativeQuestionInstanceId(input: Pick<
  CreateFormativeTaskSessionInput,
  'studentId' | 'stageId' | 'taskId' | 'contentVersion'
> & { questionId: string }): string {
  return `${createFormativeTaskSessionId(input)}:${encodeIdPart(input.questionId)}`
}

export function createFormativeTaskSession(
  input: CreateFormativeTaskSessionInput,
): FormativeTaskSession {
  const questionIds = [...input.questionIds]
  if (questionIds.length === 0 || new Set(questionIds).size !== questionIds.length) {
    throw new Error('Formative task question manifest must be non-empty and unique.')
  }
  const startedAt = input.startedAt ?? new Date().toISOString()
  return {
    ...input,
    id: createFormativeTaskSessionId(input),
    questionIds,
    phase: 'base',
    currentQuestionId: questionIds[0],
    baseOutcomes: {},
    reviewQueue: [],
    reviewOutcomes: {},
    startedAt,
    updatedAt: startedAt,
    completedAt: null,
  }
}

export function assertFormativeTaskSessionCanResume(
  session: FormativeTaskSession,
  input: CreateFormativeTaskSessionInput,
): void {
  const sameIdentity = session.studentId === input.studentId
    && session.stageId === input.stageId
    && session.unitId === input.unitId
    && session.taskId === input.taskId
    && session.contentVersion === input.contentVersion
  if (!sameIdentity) {
    throw new Error(`Formative task session ${session.id} does not match the requested identity.`)
  }
  if (session.questionIds.length !== input.questionIds.length
    || session.questionIds.some((questionId, index) => questionId !== input.questionIds[index])) {
    throw new Error(`Formative task session ${session.id} question manifest has changed.`)
  }
}

export function submitFormativeTaskAnswer(
  session: FormativeTaskSession,
  submission: FormativeTaskSubmission,
): FormativeTaskSubmissionResult {
  if (submission.correct === null && !submission.unobservedReason) {
    throw new Error('An unobserved formative question requires a reason.')
  }
  if (submission.correct !== null && submission.unobservedReason) {
    throw new Error('An observed formative answer cannot include an unobserved reason.')
  }
  const processed = [
    ...Object.entries(session.baseOutcomes),
    ...Object.entries(session.reviewOutcomes),
  ].find(([, outcome]) => outcome.submissionId === submission.submissionId)
  if (processed) {
    const [questionId, outcome] = processed
    if (questionId !== submission.questionId
      || outcome.correct !== submission.correct
      || outcome.unobservedReason !== submission.unobservedReason) {
      throw new Error(`Formative submission ${submission.submissionId} was reused with different content.`)
    }
    return { status: 'duplicate', session }
  }
  if (session.phase === 'completed') {
    throw new Error(`Formative task session ${session.id} is already completed.`)
  }
  if (submission.questionId !== session.currentQuestionId) {
    throw new Error(`Question ${submission.questionId} is not the current formative task question.`)
  }

  const submittedAt = submission.submittedAt ?? new Date().toISOString()
  const outcome = {
    submissionId: submission.submissionId,
    correct: submission.correct,
    ...(submission.unobservedReason ? { unobservedReason: submission.unobservedReason } : {}),
    submittedAt,
  }

  if (session.phase === 'review') {
    if (session.reviewOutcomes[submission.questionId]) {
      throw new Error(`Question ${submission.questionId} has already been reviewed.`)
    }
    const reviewIndex = session.reviewQueue.indexOf(submission.questionId)
    const nextQuestionId = session.reviewQueue[reviewIndex + 1] ?? null
    const completed = nextQuestionId === null
    return {
      status: 'accepted',
      session: {
        ...session,
        phase: completed ? 'completed' : 'review',
        currentQuestionId: nextQuestionId,
        reviewOutcomes: { ...session.reviewOutcomes, [submission.questionId]: outcome },
        updatedAt: submittedAt,
        completedAt: completed ? submittedAt : null,
      },
    }
  }

  if (session.baseOutcomes[submission.questionId]) {
    throw new Error(`Question ${submission.questionId} has already received its first answer.`)
  }
  const questionIndex = session.questionIds.indexOf(submission.questionId)
  const nextBaseQuestionId = session.questionIds[questionIndex + 1] ?? null
  const reviewQueue = submission.correct === false
    ? [...session.reviewQueue, submission.questionId]
    : session.reviewQueue
  const baseComplete = nextBaseQuestionId === null
  const nextQuestionId = baseComplete ? reviewQueue[0] ?? null : nextBaseQuestionId
  const phase = baseComplete
    ? reviewQueue.length > 0 ? 'review' : 'completed'
    : 'base'

  return {
    status: 'accepted',
    session: {
      ...session,
      phase,
      currentQuestionId: nextQuestionId,
      baseOutcomes: { ...session.baseOutcomes, [submission.questionId]: outcome },
      reviewQueue,
      updatedAt: submittedAt,
      completedAt: phase === 'completed' ? submittedAt : null,
    },
  }
}
